import { useMemo, useRef, useState } from "react";
import { useT, type TranslateFn } from "@/lib/translation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Turnstile from "react-turnstile";
import { toast } from "sonner";
import { Paperclip, X } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import HelpTooltip from "@/components/shared/HelpTooltip";
import type { AttachmentPayload, ContactFormData } from "@/api/client";
import { useContactForm } from "@/features/settings/hooks/useContactForm";
import { useLocale } from "@/hooks/useLocale";
import { formatFixed } from "@/lib/locale";

const MAX_FILES = 10;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_BYTES = 20 * 1024 * 1024;
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
  "text/csv",
];

interface AttachmentFile {
  name: string;
  size: number;
  base64: string;
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
  });

const createContactFormSchema = (t: TranslateFn) =>
  z.object({
    name: z.string().min(1, t("contact:validation.nameRequired")).max(200),
    reply_to_email: z
      .string()
      .email(t("contact:validation.emailInvalid"))
      .max(254),
    subject: z
      .string()
      .min(1, t("contact:validation.subjectRequired"))
      .max(300),
    message_content: z
      .string()
      .min(1, t("contact:validation.messageRequired"))
      .max(5000),
  });

type ContactFormValues = z.infer<ReturnType<typeof createContactFormSchema>>;

interface ContactUsFormProps {
  mode: "public" | "auth";
  onSuccess: () => void;
  openedAt: number;
  defaultEmail?: string;
}

export function ContactUsForm({
  mode,
  onSuccess,
  openedAt,
  defaultEmail,
}: ContactUsFormProps) {
  const t = useT();
  const { intlLocale } = useLocale();
  const contactFormSchema = useMemo(() => createContactFormSchema(t), [t]);
  const websiteRef = useRef<HTMLInputElement | null>(null); // honeypot
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const captchaEnabled = mode === "public" && !!siteKey;

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      reply_to_email: defaultEmail ?? "",
      subject: "",
      message_content: "",
    },
  });

  const sendMessageMutation = useContactForm(() => {
    setAttachments([]);
    onSuccess();
  }, mode);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";

    const invalid = files.filter((f) => !ALLOWED_TYPES.includes(f.type));
    if (invalid.length) {
      toast.error(t("contact:toast.invalidFileType"), {
        description: t("contact:toast.invalidFileTypeDescription"),
      });
      return;
    }
    if (attachments.length + files.length > MAX_FILES) {
      toast.error(t("contact:toast.tooManyFiles"), {
        description: t("contact:toast.tooManyFilesDescription", {
          max: MAX_FILES,
        }),
      });
      return;
    }
    const oversized = files.filter((f) => f.size > MAX_FILE_BYTES);
    if (oversized.length) {
      toast.error(t("contact:toast.fileTooLarge"), {
        description: t("contact:toast.fileTooLargeDescription"),
      });
      return;
    }
    const existingSize = attachments.reduce((sum, a) => sum + a.size, 0);
    const newSize = files.reduce((sum, f) => sum + f.size, 0);
    const totalSize = existingSize + newSize;
    if (totalSize > MAX_TOTAL_BYTES) {
      toast.error(t("contact:toast.totalSizeExceeded"), {
        description: t("contact:toast.totalSizeExceededDescription"),
      });
      return;
    }

    try {
      const newAttachments = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          size: f.size,
          base64: await fileToBase64(f),
        })),
      );
      setAttachments((prev) => [...prev, ...newAttachments]);
    } catch {
      toast.error(t("contact:toast.readFailed"), {
        description: t("contact:toast.tryAgain"),
      });
    }
  };

  const MAX_MESSAGE_LEN = 5000;
  const countLinks = (text: string) =>
    (text.match(/https?:\/\/|www\./gi) || []).length;

  const onSubmit = async (values: ContactFormValues) => {
    try {
      const elapsed = Date.now() - openedAt;

      if (mode === "public" && elapsed < 3000) {
        toast.error(t("contact:toast.holdOn"), {
          description: t("contact:toast.holdOnDescription"),
        });
        return;
      }

      if (captchaEnabled && !captchaToken) {
        toast.error(t("contact:toast.verificationRequired"), {
          description: t("contact:toast.verificationRequiredDescription"),
        });
        return;
      }

      if (mode === "public") {
        const msg = values.message_content || "";
        if (msg.length > MAX_MESSAGE_LEN) {
          toast.error(t("contact:toast.messageTooLong"), {
            description: t("contact:toast.messageTooLongDescription", {
              max: MAX_MESSAGE_LEN.toLocaleString(intlLocale(undefined)),
            }),
          });
          return;
        }
        if (countLinks(msg) > 3) {
          toast.error(t("contact:toast.tooManyLinks"), {
            description: t("contact:toast.tooManyLinksDescription"),
          });
          return;
        }
      }

      const attachmentPayload: AttachmentPayload[] | undefined =
        attachments.length > 0
          ? attachments.map((a) => ({ name: a.name, content: a.base64 }))
          : undefined;

      const payload: ContactFormData = (
        mode === "public"
          ? {
              ...values,
              attachments: attachmentPayload,
              hp_website: websiteRef.current?.value || "",
              elapsed_ms: elapsed,
              cf_turnstile_token: captchaToken,
            }
          : { ...values, attachments: attachmentPayload }
      ) as ContactFormData;

      sendMessageMutation.mutate(payload);
    } catch (error) {
      console.error("[ContactUsForm] Submission error:", error);
      toast.error(t("contact:toast.unexpectedError"), {
        description:
          error instanceof Error ? error.message : t("contact:toast.tryAgain"),
      });
    }
  };

  return (
    <Form {...form}>
      <form
        ref={formRef}
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        autoComplete={mode === "public" ? "off" : "on"}
        noValidate
      >
        {mode === "public" && (
          <div className="absolute left-[-9999px]" aria-hidden="true">
            <input
              type="text"
              name="hp_website"
              autoComplete="off"
              tabIndex={-1}
              ref={websiteRef}
            />
          </div>
        )}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("contact:form.nameLabel")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("contact:form.namePlaceholder")}
                  maxLength={200}
                  disabled={sendMessageMutation.isPending}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="reply_to_email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("contact:form.emailLabel")}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t("contact:form.emailPlaceholder")}
                  maxLength={254}
                  disabled={sendMessageMutation.isPending}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("contact:form.subjectLabel")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("contact:form.subjectPlaceholder")}
                  maxLength={300}
                  disabled={sendMessageMutation.isPending}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message_content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("contact:form.messageLabel")}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t("contact:form.messagePlaceholder")}
                  rows={5}
                  maxLength={5000}
                  disabled={sendMessageMutation.isPending}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {/* Attachments */}
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,application/pdf,text/csv"
            multiple
            className="hidden"
            onChange={handleFileChange}
            disabled={sendMessageMutation.isPending}
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={
                attachments.length >= MAX_FILES || sendMessageMutation.isPending
              }
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-3.5 w-3.5 mr-1.5" />
              <span>{t("contact:form.attachFiles")}</span>
              <span className="text-muted-foreground ml-1">
                ({attachments.length}/{MAX_FILES})
              </span>
            </Button>
            <HelpTooltip>
              <p>
                {t("contact:form.attachmentLimits", {
                  maxFiles: MAX_FILES,
                  perFileMb: formatFixed(MAX_FILE_BYTES / (1024 * 1024), 0),
                  totalMb: formatFixed(MAX_TOTAL_BYTES / (1024 * 1024), 0),
                })}
              </p>
              <p>{t("contact:form.supportedTypes")}</p>
            </HelpTooltip>
          </div>
          {attachments.length > 0 && (
            <ul className="space-y-1">
              {attachments.map((a, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between text-xs rounded-md border px-2 py-1.5"
                >
                  <span className="truncate text-muted-foreground">
                    {a.name}{" "}
                    <span className="opacity-60">
                      {t("contact:form.fileSizeKb", {
                        size: formatFixed(a.size / 1024, 0),
                      })}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setAttachments((prev) =>
                        prev.filter((_, idx) => idx !== i),
                      )
                    }
                    className="ml-2 text-muted-foreground hover:text-destructive shrink-0"
                    disabled={sendMessageMutation.isPending}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {captchaEnabled && (
          <div className="pt-1">
            <Turnstile
              sitekey={siteKey!}
              onVerify={(token) => setCaptchaToken(token)}
            />
          </div>
        )}
        <Button
          type="submit"
          disabled={sendMessageMutation.isPending}
          className="w-full"
        >
          {sendMessageMutation.isPending
            ? t("contact:form.sending")
            : t("contact:form.send")}
        </Button>
      </form>
    </Form>
  );
}
